// src/pages/indicadores.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { apiConfig } from "../config/api.config";

const COLORS = {
  info: "#3b82f6",
  purple: "#8b5cf6",
  danger: "#ef4444",
  gray: "#6b7280",
  green: "#10b981",
};

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatMoneyCO(v) {
  const n = toNum(v);
  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
}

function parseIndicadorCode(code) {
  const s = String(code || "").trim();
  if (!s) return { a: 99, b: 99, c: 99, d: 99, raw: "" };
  const parts = s.split(".").map((x) => parseInt(x, 10));
  return {
    a: Number.isFinite(parts[0]) ? parts[0] : 99,
    b: Number.isFinite(parts[1]) ? parts[1] : 99,
    c: Number.isFinite(parts[2]) ? parts[2] : 99,
    d: Number.isFinite(parts[3]) ? parts[3] : 99,
    raw: s,
  };
}

function compareIndicador(a, b) {
  const pa = parseIndicadorCode(a);
  const pb = parseIndicadorCode(b);
  if (pa.a !== pb.a) return pa.a - pb.a;
  if (pa.b !== pb.b) return pa.b - pb.b;
  if (pa.c !== pb.c) return pa.c - pb.c;
  if (pa.d !== pb.d) return pa.d - pb.d;
  return pa.raw.localeCompare(pb.raw);
}

// =====================================================
// MAPEO MANUAL — clave exacta = nombre de la secretaría
// =====================================================
const SECRETARIAS_MAP = {
  "Secretaría de Salud": new Set([
    "1.1.1.1","1.1.1.2","1.1.1.3","1.1.1.4",
    "1.1.2.1","1.1.2.2","1.1.2.3","1.1.2.4","1.1.2.5","1.1.2.6",
    "1.1.2.7","1.1.2.8","1.1.2.9","1.1.2.10","1.1.2.11","1.1.2.12",
    "1.1.2.13","1.1.2.14","1.1.2.15","1.1.2.16","1.1.2.17","1.1.2.18",
    "1.1.2.19","1.1.2.20",
    "1.1.3.1",
    "1.1.4.1","1.1.4.2","1.1.4.3","1.1.4.4","1.1.4.5",
    "4.3.1.1","4.3.1.2","4.3.1.3","4.3.1.5","4.3.1.6",
    "1.2.1.7",
  ]),
  "INDER Copacabana": new Set([
    "1.4.1.1","1.4.1.2","1.4.1.3","1.4.1.4","1.4.1.5","1.4.1.6",
    "1.4.1.7","1.4.1.8","1.4.1.9","1.4.1.10","1.4.1.11",
    "1.4.2.1","1.4.2.2","1.4.2.3",
    "1.4.3.1","1.4.3.3","1.4.3.4","1.4.3.5","1.4.3.6",
  ]),
  "Secretaría de Movilidad": new Set([
    "2.5.1.1","2.5.3.1","2.5.1.2","2.5.1.3","2.5.3.2",
  ]),
  "Oficina de Comunicaciones": new Set([
    "5.3.1.11","5.3.1.12","5.3.1.13",
  ]),
  "Secretaría de Servicios Administrativos": new Set([
    "5.1.2.1","5.1.2.2","5.1.2.3","5.1.2.4","5.1.2.5","5.1.2.6",
    "5.3.1.1","5.3.1.2","5.3.1.3","5.3.1.4","5.3.1.5",
    "5.3.1.6","5.3.1.7","5.3.1.8","5.3.1.10",
  ]),
  "Secretaría de Educación y Cultura": new Set([
    "1.2.1.2","1.2.1.4","1.2.1.5","1.2.1.9","1.2.1.10",
    "1.2.2.1","1.2.2.2","1.2.2.3","1.2.2.4","1.2.2.5","1.2.2.6",
    "1.2.2.7","1.2.2.8","1.2.2.9","1.2.2.10","1.2.2.12","1.2.2.13",
    "1.2.2.14","1.2.2.15","1.2.2.16","1.2.2.17","1.2.2.18","1.2.2.19",
    "1.2.2.20","1.2.2.21","1.2.2.22","1.2.2.23",
    "1.2.3.1","1.2.3.2","1.2.3.3","1.2.3.4","1.2.3.5","1.2.3.6",
    "1.2.3.7","1.2.3.8","1.2.3.10",
    "1.3.1.2","1.3.1.3",
    "1.3.2.1","1.3.2.2","1.3.2.4","1.3.2.5","1.3.2.6","1.3.2.7","1.3.2.8",
    "1.3.3.1","1.3.3.2","1.3.3.4",
    "1.3.4.1","1.3.4.2","1.3.4.3","1.3.4.4","1.3.4.5","1.3.4.6",
    "1.3.4.8","1.3.4.9","1.3.4.10","1.3.4.11",
    "2.2.1.1","3.4.3.1","4.11.2.2","4.4.1.2",
  ]),
  "Secretaría de Agricultura y Medio Ambiente": new Set([
    "2.1.1.1","2.1.1.2","2.1.1.3","2.1.1.4",
    "2.1.2.1","2.1.2.3","2.1.2.4",
    "2.1.3.1","2.1.3.3","2.1.3.4",
    "2.1.4.1","2.1.4.2","2.1.4.3","2.1.4.4",
    "2.1.5.1","2.1.5.2",
    "2.1.6.1","2.1.6.2",
    "2.2.1.1","2.2.1.4","2.2.1.5",
    "3.2.1.1","3.2.1.2","3.2.1.3","3.2.1.4",
    "3.2.2.1","3.2.2.2","3.2.2.3",
  ]),
  "Secretaría de Hacienda": new Set([
    "5.1.1.1","5.1.1.2",
  ]),
  "Secretaría de Infraestructura": new Set([
    "1.2.1.1","1.3.3.5","1.3.3.6","1.4.2.4","1.4.2.5",
    "2.1.2.2","2.1.2.5","2.2.1.2","2.3.3.2",
    "2.5.2.1","2.5.2.2",
    "2.6.2.2","2.6.2.3","2.6.2.4","2.6.2.5",
    "3.3.3.1","3.4.1.2","3.4.3.1","4.1.2.1","4.11.1.4","4.3.1.4",
    "4.4.1.6","4.7.1.5","5.3.1.9",
  ]),
  "Secretaría de Desarrollo y Bienestar Social": new Set([
    "3.1.1.1","3.1.1.2","3.1.1.3","3.1.1.4",
    "3.1.2.1","3.1.2.2","3.1.2.3","3.1.2.4",
    "3.1.3.1","3.1.3.2","3.1.3.3","3.1.3.4",
    "3.1.4.1","3.1.4.2","3.1.4.3",
    "3.1.5.1","3.1.5.2",
    "3.3.1.1",
    "3.3.2.1","3.3.2.2","3.3.2.3","3.3.2.4","3.3.2.5","3.3.2.6",
    "3.3.3.2","3.3.3.3","3.4.1.1",
    "4.1.1.1","4.1.1.2","4.1.1.3","4.1.1.4","4.1.1.5",
    "4.2.1.1",
    "4.4.1.1","4.4.1.2","4.4.1.3","4.4.1.4","4.4.1.5","4.4.1.6",
    "4.4.1.7","4.4.1.8","4.4.1.9",
    "4.5.1.1","4.5.1.2","4.5.1.3","4.5.1.4","4.5.1.5","4.5.1.7","4.5.1.8",
    "4.6.1.1","4.6.1.2","4.6.1.3","4.6.1.4","4.6.1.5","4.6.1.6",
    "4.7.1.1","4.7.1.2","4.7.1.3","4.7.1.4","4.7.1.5","4.7.1.6","4.7.1.7",
    "4.8.1.1","4.8.1.2","4.8.1.3",
    "4.9.1.1","4.9.1.2","4.9.1.3","4.9.1.4","4.9.1.5",
    "4.10.1.1","4.10.1.2","4.10.1.3","4.10.1.4","4.10.1.5","4.10.1.6",
    "5.2.1.1","5.2.1.2","5.2.1.3","5.2.1.4","5.2.1.5","5.2.1.6","5.2.1.7",
  ]),
  "Secretaría de Gobierno": new Set([
    "2.3.1.1","2.3.1.3","2.3.2.1","2.3.3.1","2.3.3.3","3.3.3.2",
    "4.11.1.1","4.11.1.2","4.11.1.3","4.11.1.4","4.11.1.5","4.11.1.6",
    "4.11.1.7","4.11.1.8","4.11.1.9","4.11.1.10","4.11.1.11","4.11.1.12",
    "4.11.1.15",
    "4.11.2.1","4.11.2.3","4.11.2.4","4.11.3.2",
  ]),
  "INVICOP": new Set([
    "2.4.1.1","2.4.1.2","2.4.1.3","2.4.1.4",
  ]),
  "Departamento Administrativo de Planeación": new Set([
    // ✅ Incluye 3.4.4.2 que existe en contratos
    "1.1.1.4","2.3.2.1","5.1.2.4","2.6.1.1","3.4.4.1","3.4.4.2",
  ]),
};

// =====================================================
// NORMALIZACIÓN: valor del API → clave de SECRETARIAS_MAP
// =====================================================
const NORMALIZE_MAP = {
  "secretaría de salud":                         "Secretaría de Salud",
  "secretaria de salud":                         "Secretaría de Salud",
  "inder copacabana":                            "INDER Copacabana",
  "secretaría de movilidad":                     "Secretaría de Movilidad",
  "secretaria de movilidad":                     "Secretaría de Movilidad",
  "oficina de comunicaciones":                   "Oficina de Comunicaciones",
  "secretaría de servicios administrativos":     "Secretaría de Servicios Administrativos",
  "secretaria de servicios administrativos":     "Secretaría de Servicios Administrativos",
  "secretaría de educación y cultura":           "Secretaría de Educación y Cultura",
  "secretaria de educacion y cultura":           "Secretaría de Educación y Cultura",
  "secretaría de agricultura y medio ambiente":  "Secretaría de Agricultura y Medio Ambiente",
  "secretaria de agricultura y medio ambiente":  "Secretaría de Agricultura y Medio Ambiente",
  "secretaría de hacienda":                      "Secretaría de Hacienda",
  "secretaria de hacienda":                      "Secretaría de Hacienda",
  "secretaría de infraestructura":               "Secretaría de Infraestructura",
  "secretaria de infraestructura":               "Secretaría de Infraestructura",
  "secretaría de desarrollo y bienestar social": "Secretaría de Desarrollo y Bienestar Social",
  "secretaria de desarrollo y bienestar social": "Secretaría de Desarrollo y Bienestar Social",
  "secretaría de gobierno":                      "Secretaría de Gobierno",
  "secretaria de gobierno":                      "Secretaría de Gobierno",
  "invicop":                                     "INVICOP",
  "departamento administrativo de planeación":   "Departamento Administrativo de Planeación",
  "departamento administrativo de planeacion":   "Departamento Administrativo de Planeación",
  "salud":          "Secretaría de Salud",
  "inder":          "INDER Copacabana",
  "movilidad":      "Secretaría de Movilidad",
  "comunicaciones": "Oficina de Comunicaciones",
  "servicios":      "Secretaría de Servicios Administrativos",
  "servicios administrativos": "Secretaría de Servicios Administrativos",
  "educacion":      "Secretaría de Educación y Cultura",
  "educación":      "Secretaría de Educación y Cultura",
  "agricultura":    "Secretaría de Agricultura y Medio Ambiente",
  "hacienda":       "Secretaría de Hacienda",
  "infraestructura":"Secretaría de Infraestructura",
  "desarrollo":     "Secretaría de Desarrollo y Bienestar Social",
  "gobierno":       "Secretaría de Gobierno",
  "planeacion":     "Departamento Administrativo de Planeación",
  "planeación":     "Departamento Administrativo de Planeación",
};

function normalizeSecretaria(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (SECRETARIAS_MAP[trimmed]) return trimmed;
  const lower = trimmed.toLowerCase();
  if (NORMALIZE_MAP[lower]) return NORMALIZE_MAP[lower];
  const sinTildes = lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (NORMALIZE_MAP[sinTildes]) return NORMALIZE_MAP[sinTildes];
  return trimmed;
}

function secretariaFromLista(code) {
  const c = String(code || "").trim();
  if (!c) return "SIN_SECRETARIA";
  for (const [sec, set] of Object.entries(SECRETARIAS_MAP)) {
    if (set.has(c)) return sec;
  }
  return "SIN_SECRETARIA";
}

export default function Indicadores() {
  const [loading, setLoading] = useState(true);
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState("");
  const [filtroFecha, setFiltroFecha]           = useState("todo");
  const [filtroSecretaria, setFiltroSecretaria] = useState("todas");

  const cargar = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);
      const url = apiConfig.getUrl(
        "/api/indicadores.php",
        `filtro=${encodeURIComponent(filtroFecha)}&t=${Date.now()}`
      );
      const resp = await fetch(url, { credentials: "include" });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.ok) {
        throw new Error(json?.error || json?.message || `Error ${resp.status}`);
      }
      setData(json.data || null);
    } catch (e) {
      setData(null);
      setErr(e?.message || "No se pudo cargar Indicadores");
    } finally {
      setLoading(false);
    }
  }, [filtroFecha]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    const id = setInterval(() => cargar(), 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [cargar]);

  // ===== TABLA =====
  const tabla = useMemo(() => {
    const rows = Array.isArray(data?.rows) ? data.rows : [];

    // 1. Normalizar secretaría de cada fila
    //    (el PHP ya devuelve una fila por indicador+secretaria)
    const rowsNorm = rows.map((r) => ({
      ...r,
      secretaria: normalizeSecretaria(r?.secretaria) || secretariaFromLista(r?.indicador),
    }));

    // 2. Filtrar por secretaría creadora del contrato
    const rowsFiltrados = filtroSecretaria === "todas"
      ? rowsNorm
      : rowsNorm.filter((r) => r.secretaria === filtroSecretaria);

    // 3. Agrupar por indicador (suma por si acaso vienen duplicados)
    const agrupado = {};
    rowsFiltrados.forEach((r) => {
      const ind = String(r?.indicador ?? "").trim();
      if (!ind) return;
      const count = toNum(r?.contratos_aprobados ?? r?.count ?? 0);
      const pres  = toNum(r?.presupuesto_total ?? r?.presupuesto ?? 0);

      if (!agrupado[ind]) {
        agrupado[ind] = { indicador: ind, secretaria: r.secretaria, count: 0, presupuesto_total: 0 };
      }
      agrupado[ind].count            += count;
      agrupado[ind].presupuesto_total += pres;
    });

    // 4. Con filtro de secretaría: mostrar TODOS los indicadores del mapa,
    //    incluyendo los que tienen 0 contratos.
    if (filtroSecretaria !== "todas") {
      const codigosSecretaria = Array.from(SECRETARIAS_MAP[filtroSecretaria] || []);
      const resultado = codigosSecretaria.map((codigo) => {
        const ex = agrupado[codigo];
        return {
          indicador:        codigo,
          secretaria:       filtroSecretaria,
          count:            ex ? Math.round(ex.count) : 0,
          presupuesto_total: ex ? ex.presupuesto_total : 0,
        };
      });
      resultado.sort((a, b) => compareIndicador(a.indicador, b.indicador));
      return resultado;
    }

    // 5. "Todas": devolver agrupado
    const base = Object.values(agrupado);
    base.sort((a, b) => compareIndicador(a.indicador, b.indicador));
    return base;
  }, [data, filtroSecretaria]);

  // ===== KPIs =====
  const kpis = useMemo(() => {
    const calcFromTabla = () => ({
      total_aprobados:    Math.round(tabla.reduce((a, r) => a + toNum(r.count), 0)),
      valor_total:        tabla.reduce((a, r) => a + toNum(r.presupuesto_total), 0),
      con_indicador_count: tabla.filter((r) => toNum(r.count) > 0).length,
      con_indicador_valor: tabla.reduce(
        (a, r) => a + (toNum(r.presupuesto_total) > 0 ? toNum(r.presupuesto_total) : 0), 0
      ),
    });

    if (filtroSecretaria !== "todas") return calcFromTabla();

    const apiKpis = data?.kpis;
    if (apiKpis && typeof apiKpis === "object") {
      return {
        total_aprobados:    toNum(apiKpis.total_aprobados),
        valor_total:        toNum(apiKpis.presupuesto_total_aprobado),
        con_indicador_count: toNum(apiKpis.con_indicador_count),
        con_indicador_valor: toNum(apiKpis.con_indicador_valor),
      };
    }
    return calcFromTabla();
  }, [data, tabla, filtroSecretaria]);

  const secretariasDisponibles = useMemo(
    () => Object.keys(SECRETARIAS_MAP).sort((a, b) => a.localeCompare(b, "es")),
    []
  );

  if (loading) {
    return (
      <div style={{ display: "flex", minHeight: "60vh", alignItems: "center", justifyContent: "center" }}>
        <div style={{
          width: 48, height: 48,
          border: "4px solid #e5e7eb", borderTop: "4px solid #3b82f6",
          borderRadius: "50%", animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto", backgroundColor: "#f9fafb", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.5rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: "1.7rem", fontWeight: 900, color: "#111827", margin: 0 }}>
              📌 Plan de Desarrollo – Indicadores
            </h1>
            <p style={{ color: "#6b7280", margin: "0.35rem 0 0 0", fontSize: "1rem", fontWeight: 700 }}>
              Actualiza automáticamente cada 10 minutos.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <select value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)} style={selectStyle}>
              <option value="todo">Todo el tiempo</option>
              <option value="30dias">Últimos 30 días</option>
              <option value="90dias">Últimos 90 días</option>
              <option value="año">Último año</option>
            </select>
            <select value={filtroSecretaria} onChange={(e) => setFiltroSecretaria(e.target.value)} style={selectStyle}>
              <option value="todas">Todas las secretarías</option>
              {secretariasDisponibles.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              <option value="SIN_SECRETARIA">SIN_SECRETARIA</option>
            </select>
            <button onClick={cargar} style={btnStyle}>
              <RefreshCw size={18} />
              Refrescar
            </button>
          </div>
        </div>
        {err && <div style={{ marginTop: "0.9rem", color: COLORS.danger, fontWeight: 900 }}>{err}</div>}
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <Kpi title="Total contratos aprobados"  value={`${kpis.total_aprobados}`}                 color={COLORS.info}   />
        <Kpi title="Presupuesto aprobado total" value={`$ ${formatMoneyCO(kpis.valor_total)}`}    color={COLORS.green}  />
        <Kpi title="Indicadores con contratos"  value={`${kpis.con_indicador_count}`}             color={COLORS.info}   />
        <Kpi title="Presupuesto con indicador"  value={`$ ${formatMoneyCO(kpis.con_indicador_valor)}`} color={COLORS.purple} />
      </div>

      {/* Tabla */}
      <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "#111827" }}>
            {filtroSecretaria === "todas" ? "Resumen por indicador" : `Indicadores – ${filtroSecretaria}`}
          </div>
          <div style={{ color: COLORS.gray, fontWeight: 800 }}>
            Indicadores: {tabla.length}
            {filtroSecretaria !== "todas" && (
              <span style={{ marginLeft: "0.75rem", color: COLORS.info }}>
                Con contratos: {tabla.filter((r) => r.count > 0).length}
              </span>
            )}
          </div>
        </div>

        {tabla.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280", fontWeight: 800 }}>
            <FileText size={42} style={{ marginBottom: "0.5rem" }} />
            <div>No hay datos para mostrar.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <Th>Código</Th>
                  {filtroSecretaria === "todas" && <Th>Secretaría</Th>}
                  <Th align="center">Contratos aprobados</Th>
                  <Th align="right">Presupuesto aprobado</Th>
                </tr>
              </thead>
              <tbody>
                {tabla.map((r, idx) => {
                  const sinContratos = r.count === 0;
                  const rowBg     = sinContratos ? "#fafafa" : idx % 2 === 0 ? "white" : "#f9fafb";
                  const colorTexto = sinContratos ? "#9ca3af" : "inherit";
                  return (
                    <tr key={`${r.indicador}-${r.secretaria}-${idx}`} style={{ backgroundColor: rowBg }}>
                      <Td strong style={{ color: colorTexto, fontFamily: "monospace", fontSize: "0.9rem" }}>
                        {r.indicador}
                      </Td>
                      {filtroSecretaria === "todas" && (
                        <Td style={{ color: colorTexto }}>{r.secretaria}</Td>
                      )}
                      <Td strong style={{ color: colorTexto, textAlign: "center" }}>
                        {Math.round(r.count)}
                      </Td>
                      <Td strong style={{ color: colorTexto, textAlign: "right" }}>
                        {r.presupuesto_total === 0 ? "$ 0" : `$ ${formatMoneyCO(r.presupuesto_total)}`}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const selectStyle = {
  padding: "0.6rem 1rem", border: "1px solid #d1d5db",
  borderRadius: "10px", fontSize: "1rem", cursor: "pointer", fontWeight: 800,
};

const btnStyle = {
  padding: "0.6rem 1rem", backgroundColor: "#8b5cf6", color: "white",
  border: "none", borderRadius: "10px", fontSize: "1rem", cursor: "pointer",
  display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 800,
};

function Kpi({ title, value, color }) {
  return (
    <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.25rem" }}>
      <div style={{ fontSize: "1rem", color: "#6b7280", fontWeight: 800 }}>{title}</div>
      <div style={{ fontSize: "2rem", fontWeight: 900, color, marginTop: "0.4rem" }}>{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{ textAlign: align, padding: "10px", borderBottom: "1px solid #e5e7eb", fontWeight: 900, whiteSpace: "nowrap", fontSize: "0.95rem" }}>
      {children}
    </th>
  );
}

function Td({ children, strong = false, style = {} }) {
  return (
    <td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", fontWeight: strong ? 900 : 800, whiteSpace: "nowrap", verticalAlign: "middle", ...style }}>
      {children}
    </td>
  );
}